import { Icon, Popup } from 'semantic-ui-react';
import { formatCurrency } from '../services';
import NotesBillableHeart from './NotesBillableHeart';

const GREATER_OF_CONTRACTS = ['Greater Of', 'Greater Of w/HYN', 'GOX', 'GOZ'];

/**
 * PracticeRow — Level-1 expansion row (practice level) inside CdpReportsTableByDentists.
 *
 * Props mirror the local variables computed inside getTableRows() for each practice
 * iteration, plus hideColumn and callbacks.
 */
const PracticeRow = (props) => {
    const {
        hideColumn,
        row,
        practice,
        practiceName,
        expandableRowLevel2,
        showAsterisk,
        workedDaysExt,
        scheduledDays,
        rtoApproved,
        rtoForced,
        patientVisits,
        bPCV,
        patientVisitsPerDay,
        rampStartDate,
        rampEndDate,
        practiceCare,
        practiceWriteOff,
        practiceAdjustment,
        prodAdjWo,
        practiceBCare,
        practiceHynBillCare,
        levelTwoIcon,
        levelTwoKey,
        customProviderRates,
        pracHynContract,
        isRampApplicable,
        expandRowLevelTwo,
        hasPayOverride,
        onOpenOverride,
        onDeleteOverride,
        enableBcareOverride,
        highlightBaseStatus,
    } = props;

    const practiceData = row.data[practice] || {};
    const practiceHygienistPayContribution = practiceHynBillCare > 0
        ? practiceHynBillCare * ((practiceData.practicePayOverBase || 0) / 100)
        : 0;

    return (
    <tr>
        {!hideColumn.selectAll && <td className={"expandedLevelOneCell"}>{''}</td>}
        {!hideColumn.lockStatus && <td className={"expandedLevelOneCell"}>{''}</td>}
        {!hideColumn.provider && <td className={"expandedLevelOneCell"}>{''}</td>}
        {!hideColumn.ABBR && <td className={"expandedLevelOneCell"}>{row.ABBR}</td>}
        {!hideColumn.practice && <td className={"expandedLevelOneCell"}>
            <Icon
                size='small'
                className={`${levelTwoIcon} red`}
                onClick={() => expandRowLevelTwo(levelTwoKey)}
            />
            <span
                className={
                    isRampApplicable['isPracticeOnRampThisMonth']
                        ? (isRampApplicable['isProviderOwnerOfPractice']
                            ? "boldRed"
                            : "orange")
                        : "regularBlack"
                }
                style={{
                    textDecoration: customProviderRates ? "underline" : "",
                    fontStyle: pracHynContract ? 'italic' : '',
                }}
            >
                {practiceName}
            </span>
            {hasPayOverride && (
                <Icon
                    name='lock'
                    size='small'
                    title='bCare pay forced — click to remove'
                    style={{
                        marginLeft: 6,
                        cursor: 'pointer',
                        color: 'orange'
                    }}
                    onClick={() => onDeleteOverride && onDeleteOverride()}
                />
            )}
            {!hasPayOverride && enableBcareOverride && GREATER_OF_CONTRACTS.includes(row.contractType) && (
                <Icon
                    name='lock open'
                    size='small'
                    title='No override (click to set)'
                    style={{
                        marginLeft: 6,
                        cursor: 'pointer',
                        color: '#aaa'
                    }}
                    onClick={() => onOpenOverride && onOpenOverride()}
                />
            )}
        </td>}
        {!hideColumn.contractType && <td className={"expandedLevelOneCell"}></td>}
        {!hideColumn.workTimeType &&
            <td className={"expandedLevelOneCell"}>{row.dentistWorkTimeType}</td>}
        {!hideColumn.workedDays &&
            <td className={"expandedLevelOneCell"}> {workedDaysExt}</td>}
        {!hideColumn.scheduledDays &&
            <td className={"expandedLevelOneCell"}> {scheduledDays}</td>}
        {!hideColumn.RTOApproved &&
            <td className={"expandedLevelOneCell"}>{rtoApproved}</td>}
        {!hideColumn.FTOApproved &&
            <td className={"expandedLevelOneCell"}>{rtoForced}</td>}
        {!hideColumn.patientVisits &&
            <td className={"expandedLevelOneCell"}>{patientVisits}</td>}
        {!hideColumn.bCPV &&
            <td className={"expandedLevelOneCell"}>{formatCurrency(bPCV)}</td>}
        {!hideColumn.patientVisitsPerDay &&
            <td className={"expandedLevelOneCell"}>{patientVisitsPerDay}</td>}
        {!hideColumn.rampStartDate &&
            <td className={"expandedLevelOneCell"}>{rampStartDate}</td>}
        {!hideColumn.rampEndDate &&
            <td className={"expandedLevelOneCell"}> {rampEndDate} </td>}
        {!hideColumn.procedureDate && expandableRowLevel2 &&
            <td className={"expandedLevelOneCell"}>{''}</td>}
        {!hideColumn.startEnd && expandableRowLevel2 &&
            <td className={"expandedLevelOneCell"}>{''}</td>}
        {!hideColumn.care &&
            <td className={"expandedLevelOneCell"}> {formatCurrency(practiceCare)}</td>}
        {!hideColumn.writeOffs &&
            <td className={"expandedLevelOneCell"}>{formatCurrency(practiceWriteOff * -1)}</td>}
        {!hideColumn.adjustments &&
            <td className={"expandedLevelOneCell"}>{formatCurrency(practiceAdjustment)}</td>}
        {!hideColumn.careFraction &&
            <td className={"expandedLevelOneCell"}>{prodAdjWo}%</td>}
        {(!hideColumn.collectionsGoxGozTotal && row.contractType !== 'Collections w/HYN') &&
            <td className={"expandedLevelOneCell"}>
                {(row.contractType === 'Collections' || row.contractType === 'Collections Offset')
                    ? formatCurrency(parseFloat(row.data[practice].collectionsNonBillableCare || 0))
                    : (row.contractType === 'GOZ' || row.contractType === 'GOX')
                        ? formatCurrency(parseFloat(row.data[practice].goxNonBillableCare || 0))
                        : row.data[practice]?.rawTedCollections > 0
                            ? formatCurrency(parseFloat(row.data[practice].rawTedCollections))
                            : ''}
            </td>}
        {(!hideColumn.collectionsGoxGozTotal && row.contractType === 'Collections w/HYN') &&
            <td className={"expandedLevelOneCell"}>
                {formatCurrency(parseFloat(row.data[practice].collectionsNonBillableCare || 0))}
                {showAsterisk && ' *' + formatCurrency(parseFloat(row.data[practice].pracHynCollections || '')) + '*'}
            </td>}
        {!hideColumn.bCare &&
            <td className={"expandedLevelOneCell"}>
                {formatCurrency(practiceBCare + row.data[practice].billableNotes)}
                {showAsterisk && practiceHynBillCare !== 0 ? ` *${formatCurrency(practiceHynBillCare)}*` : ''}
            </td>}
        {hideColumn.totalBasePay ? '' :
            <td className={"expandedLevelOneCell"}>
                {row.data[practice]?.practicePayBasedOnDailyRate && row.contractType !== 'Prod Hurdle'
                    ? formatCurrency(row.data[practice].practicePayBasedOnDailyRate || 0)
                    : '-'}
                {row.data[practice]?.payEstimated &&
                    <Popup
                        trigger={<Icon name='calculator' color='blue' className='ml-1' />}
                        content='Estimated pay — re-imagined from Dentist/Oral Surgeon days only, not an official figure.'
                    />
                }
                {row.data[practice]?.basePayNotDayFiltered &&
                    <Popup
                        trigger={<Icon name='info circle' color='grey' className='ml-1' />}
                        content='Reflects the full period — not narrowed by the Dentist/Oral Surgeon day filter.'
                    />
                }
            </td>}
        {!hideColumn.financialNotes &&
            <td className={"expandedLevelOneCell"}>
                {row.data[practice]?.financialNotes
                    ? formatCurrency(row.data[practice].financialNotes || 0)
                    : '-'}
                <NotesBillableHeart amount={row.data[practice]?.billableNotes} scope="practice" />
            </td>}
        {!hideColumn.totalPay &&
            <td className={"expandedLevelOneCell"}>
                {(row.contractType === 'UKG HYN' || row.contractType === 'TMPHYN' || row.contractType === 'TMPDOC')
                    ? (practiceData && formatCurrency(practiceData.financialNotes || 0))
                    : (practiceData && (
                        <span style={{ color: (highlightBaseStatus && row.contractType !== 'Prod Hurdle') ? (practiceData.isPracticeOverBase === true ? 'green' : practiceData.isPracticeOverBase === false ? 'red' : undefined) : undefined }}>
                            {formatCurrency(practiceData.practiceTotalPay || 0)}
                            {practiceData.payEstimated &&
                                <Popup
                                    trigger={<Icon name='calculator' color='blue' className='ml-1' />}
                                    content='Estimated pay — re-imagined from Dentist/Oral Surgeon days only, not an official figure.'
                                />
                            }
                            {showAsterisk && practiceHygienistPayContribution > 0
                                ? <><br />*{formatCurrency(practiceHygienistPayContribution)}*</>
                                : ''}
                        </span>
                    ))}
            </td>}
        {!hideColumn.normalizedPercentageOfBillableCare &&
            <td className={"expandedLevelOneCell"}></td>}
    </tr>
    );
};

export default PracticeRow;
